import React, { useCallback, useState } from 'react';

import { FormattedMessage } from 'react-intl';

import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import ModalDialog, {
  ModalTitle,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@atlaskit/modal-dialog';

import { useIntl } from 'src/hooks/intl';

import diffMessages from './diff.i18n';

type DialogConfig = {
  message: FormattedMessage.MessageDescriptor;
  onCancelDiscard?: () => void;
  onConfirmDiscard: () => void;
};

export const useDiscardCommentsDialogState = () => {
  const [config, setConfig] = useState<DialogConfig | null>(null);

  const confirmDiscardComments = useCallback(
    (cfg: DialogConfig) => setConfig(cfg),
    []
  );

  const onCancel = useCallback(() => {
    config?.onCancelDiscard?.();
    setConfig(null);
  }, [config]);

  const onDiscard = useCallback(() => {
    if (!config) {
      return;
    }
    config.onConfirmDiscard();
    setConfig(null);
  }, [config]);

  const dialogProps: Props = {
    children: config ? (
      <FormattedMessage {...config.message} tagName="p" />
    ) : null,
    onCancel,
    onDiscard,
  };

  return { confirmDiscardComments, dialogProps, isDialogOpen: !!config };
};

type Props = {
  children: React.ReactNode;
  onDiscard: () => void;
  onCancel: () => void;
};

const DiscardCommentsDialog = React.memo((props: Props) => {
  const intl = useIntl();
  const { onDiscard, onCancel, children } = props;

  return (
    <ModalDialog width="small" onClose={onCancel}>
      <ModalHeader>
        <ModalTitle appearance="danger">
          {intl.formatMessage(diffMessages.discardCommentsModalTitle)}
        </ModalTitle>
      </ModalHeader>
      <ModalBody>{children}</ModalBody>
      <ModalFooter>
        <CustomThemeButton appearance="danger" onClick={onDiscard}>
          {intl.formatMessage(diffMessages.discardCommentsModalDiscardButton)}
        </CustomThemeButton>
        <CustomThemeButton appearance="subtle" onClick={onCancel}>
          {intl.formatMessage(diffMessages.discardCommentsModalCancelButton)}
        </CustomThemeButton>
      </ModalFooter>
    </ModalDialog>
  );
});

export default DiscardCommentsDialog;
